// src/App.tsx
import React, { useEffect, useState } from "react";
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
} from "@azure/msal-browser";
import { msalConfig, tokenRequest } from "./authConfig";

import LandingPage from "./components/LandingPage";
import BusinessAreasLanding from "./components/BusinessAreasLanding";
import CategoryLanding from "./components/CategoryLanding";
import ProcessViewer from "./components/ProcessViewer";

import { ThemeProvider } from "./components/themeProvider";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import useSessionState from "@/hooks/useSessionState";
import { resolveBusinessAreaName } from "@/config/businessAreasConfig";

const msalInstance = new PublicClientApplication(msalConfig);

function App() {
  const { toast } = useToast();
  const [loggedIn, setLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState("");
  const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(null);

  // Initiera MSAL
  useEffect(() => {
    (async () => {
      try {
        if (typeof msalInstance.initialize === "function") {
          await msalInstance.initialize();
        }
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          setActiveAccount(accounts[0]);
          const res = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account: accounts[0],
          });
          setAccessToken(res.accessToken);
          setLoggedIn(true);
          toast({
            title: "Inloggad",
            description: `Välkommen ${accounts[0].name || accounts[0].username}`,
            duration: 3000,
          });
        } else {
          await loginPopup();
        }
      } catch (err: any) {
        if (err instanceof InteractionRequiredAuthError) {
          await loginPopup();
        } else {
          console.error(err);
          toast({
            title: "Login error",
            description: err.message,
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const loginPopup = async () => {
    setIsLoading(true);
    try {
      const resp = await msalInstance.loginPopup(tokenRequest);
      msalInstance.setActiveAccount(resp.account!);
      setActiveAccount(resp.account!);
      const tokenRes = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: resp.account!,
      });
      setAccessToken(tokenRes.accessToken);
      setLoggedIn(true);
      toast({
        title: "Inloggad",
        description: `Välkommen ${resp.account?.name || resp.account?.username}`,
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Login error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Navigationsstate:
  // null = visa CategoryLanding
  // string (inkl "" eller annan text) = visa ProcessViewer
  const [selectedBusinessArea, setSelectedBusinessArea] =
    useSessionState<string | null>("selectedBusinessArea", null);
  const [selectedCategory, setSelectedCategory] =
    useSessionState<string | null>("selectedCategory", null);

  const [isNavigating, setIsNavigating] = useState(false);

  const navigateToArea = (area: string) => {
    if (isNavigating) return;
    setIsNavigating(true);
    const normalized = resolveBusinessAreaName(area);
    setSelectedBusinessArea(normalized);
    setSelectedCategory(null); // gå till CategoryLanding
    setTimeout(() => setIsNavigating(false), 300);
    toast({
      title: "Affärsområde valt",
      description: `Visar processer för ${normalized}`,
      duration: 2000,
    });
  };

  const navigateToCategory = (category: string | null) => {
    if (isNavigating) return;
    setIsNavigating(true);
    setSelectedCategory(category); // null = standard, string = vald kategori
    setTimeout(() => setIsNavigating(false), 300);
    toast({
      title: category ? "Kategori vald" : "Standardprocesser valda",
      description: category
        ? `Visar processer för ${selectedBusinessArea} - ${category}`
        : `Visar standardprocesser för ${selectedBusinessArea}`,
      duration: 2000,
    });
  };

  const resetToAreas = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setSelectedBusinessArea(null);
    setSelectedCategory(null);
    setTimeout(() => setIsNavigating(false), 300);
  };

  const resetToCategories = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setSelectedCategory(null);
    setTimeout(() => setIsNavigating(false), 300);
  };

  if (!loggedIn) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <LandingPage
          handleLogin={loginPopup}
          isLoading={isLoading}
          activeAccount={activeAccount}
        />
        <Toaster />
      </ThemeProvider>
    );
  }

  if (!selectedBusinessArea) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <BusinessAreasLanding
          accessToken={accessToken}
          onSelectArea={navigateToArea}
        />
        <Toaster />
      </ThemeProvider>
    );
  }

  // Visa kategori-selector när selectedCategory === null
  if (selectedCategory === null) {
    return (
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <CategoryLanding
          accessToken={accessToken}
          businessArea={selectedBusinessArea}
          onCategorySelect={navigateToCategory}
          onBackToAreas={resetToAreas}
        />
        <Toaster />
      </ThemeProvider>
    );
  }

  // Annars visa ProcessViewer
  return (
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <ProcessViewer
        accessToken={accessToken}
        businessArea={selectedBusinessArea}
        category={selectedCategory}
        onBackToAreas={resetToAreas}
        onBackToCategories={resetToCategories}
      />
      <Toaster />
    </ThemeProvider>
  );
}

export default App;
